import { yupResolver } from '@hookform/resolvers/yup';
import { FC, FocusEvent, useEffect, useMemo, useState } from 'react';
import { DeepPartialSkipArrayKey, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLoaderData } from 'react-router-dom';
import { Select } from '../../../../../../shared/components/form';
import { ISelectOption } from '../../../../../../shared/components/form/fields/select.component';
import {
  createNestedObject,
  getJSONValueByPath,
  getParentName,
  mergeDeepObjects,
} from '../../../../../../shared/helpers/utils/utils.helper';
import { Wa_FontDescriptionInput } from '../../../../../../graphql/generatedModel';
import { ReportsConfigurationRes } from '../../../services/printForms.service';
import {
  IReportsConfigurationTabState,
  reportsConfigurationTabState,
} from '../../../states/reportsConfiguration.state';
import { ITabState } from '../../../states/tabState.model';
import { ArticlePricelistConfig } from './components/articlePricelistConfig';
import { OfferConfig } from './components/offerConfig';
import { DeliveryNoteA4Config } from './components/deliveryNoteA4Config';
import { DeliveryNoteA5Config } from './components/deliveryNoteA5Config';
import { InvoiceConfig } from './components/invoiceConfig';
import { PaymentSlipConfig } from './components/paymentSlipConfig';
import { DebtReminderConfig } from './components/debtReminderConfig';
import { ReportPreviewArea } from './components/reportPreviewArea';
import { createValidationSchema } from './reportsConfiguration.schema';
import { Content } from '../../../../../../shared/components/content/content.component';
import { Box, Stack, styled } from '@mui/material';
import { CommonMenu } from './components/commonMenu/commonMenu.tsx';
import { printFormsService } from '../../../services/printForms.service.ts';
import { responseHandler } from '../../../../../../shared/responseHandler/responseHandler.ts';
import { ContentLoading } from '../../../../../../shared/components/loading/loading.component.tsx';

export enum ConfigurablePrintFormTypes {
  ARTICLE_PRICELIST,
  OFFER,
  DELIVERY_NOTE_A4,
  DELIVERY_NOTE_A5,
  INVOICE,
  PAYMENT_SLIP,
  DEBT_REMINDER,
}

const validationSchema = createValidationSchema();

export const ReportsConfigurationTab: FC = () => {
  const defaultPrintFormType = ConfigurablePrintFormTypes.ARTICLE_PRICELIST;
  const [printFormType, setPrintFormType] = useState(defaultPrintFormType);
  const [expandedState, setExpandedState] = useState(true);
  const [subCompanyDataLoading, setSubCompanyDataLoading] = useState(false);

  const { t } = useTranslation();
  const {
    inputDataForReportsPreview,
    reportConfiguration: initTabData,
    subCompaniesOptions,
  } = useLoaderData() as ITabState;

  const formMethods = useForm<ReportsConfigurationRes>({
    mode: 'onChange',
    defaultValues: initTabData,
    resolver: yupResolver(validationSchema),
  });

  const {
    watch,
    reset,
    formState: { errors, dirtyFields, isDirty },
  } = formMethods;

  useEffect(() => {
    const formStateSub = watch((data, reason) => {
      if (reason.type === 'change' && reason.name) {
        const fieldPath = reason.name;
        // Font settings are serialized together on the backend (WA_FontDescriptionInput
        // requires family + size), so a partial fontOverride is rejected. Record the whole
        // fontOverride object whenever any of its parts changes (WA-3723).
        if (checkIsAdditionalFieldRequired(fieldPath)) {
          const dataToSave = formDataWithAdditionalFields(fieldPath, data);
          if (dataToSave) reportsConfigurationTabState.pub.recordData(dataToSave);
        } else {
          const fieldValue = getJSONValueByPath(data, fieldPath);
          const dataToSave = createNestedObject(fieldPath, fieldValue);
          dataToSave['subCompanyId'] = formMethods.getValues().subCompanyId;
          reportsConfigurationTabState.pub.recordData(dataToSave);
        }
      }
    });

    const reportsConfigurationTabStateSub = reportsConfigurationTabState.sub
      .state()
      .subscribe(({ dataToSave }) => {
        reset((currentValues) => {
          const res = mergeDeepObjects(currentValues, dataToSave);
          return res;
        });
      });

    const printFormsServiceSub = printFormsService.sub
      .getReportsConfiguration()
      .pipe(responseHandler<ReportsConfigurationRes>({ errorReturnType: undefined }))
      .subscribe((data) => {
        reset(data);
        setSubCompanyDataLoading(false);
      });

    return () => {
      formStateSub.unsubscribe();
      reportsConfigurationTabStateSub.unsubscribe();
      printFormsServiceSub.unsubscribe();
    };
  }, []);

  const subCompOptions: ISelectOption[] = useMemo(
    () => subCompaniesOptions.map((item) => ({ id: item.id, label: item.label ?? '' })),
    [subCompaniesOptions],
  );

  const printFormsTypesOptions: ISelectOption[] = useMemo(
    () => [
      {
        id: ConfigurablePrintFormTypes.ARTICLE_PRICELIST,
        label: t('settings.print_form_article_pricelist'),
      },
      {
        id: ConfigurablePrintFormTypes.OFFER,
        label: t('settings.print_form_offer'),
      },
      {
        id: ConfigurablePrintFormTypes.DELIVERY_NOTE_A4,
        label: t('settings.print_form_delivery_note_a4'),
      },
      {
        id: ConfigurablePrintFormTypes.DELIVERY_NOTE_A5,
        label: t('settings.print_form_delivery_note_a5'),
      },
      {
        id: ConfigurablePrintFormTypes.INVOICE,
        label: t('settings.print_form_invoice'),
      },
      {
        id: ConfigurablePrintFormTypes.PAYMENT_SLIP,
        label: t('settings.print_form_payment_slip'),
      },
      {
        id: ConfigurablePrintFormTypes.DEBT_REMINDER,
        label: t('settings.print_form_debt_reminder'),
      },
    ],
    [t],
  );

  const handlePrintFormTypeChange = (value: ISelectOption | null) => {
    if (value) {
      setPrintFormType(value.id as ConfigurablePrintFormTypes);
    }
  };

  const handleSubCompanyChange = (value: ISelectOption | null) => {
    printFormsService.pub.getReportsConfiguration({
      subCompanyId: value ? value.id.toString() : undefined,
    });
    setSubCompanyDataLoading(true);
  };

  const onSubmit = (e: FocusEvent<HTMLFormElement>) => {
    const parentName = getParentName(e);
    const targetName = e.target.name || parentName;
    const needSave =
      !getJSONValueByPath(errors, targetName) && !!getJSONValueByPath(dirtyFields, targetName);

    if (needSave) {
      if (checkIsAdditionalFieldRequired(targetName)) {
        const dataToSave = formDataWithAdditionalFields(targetName, formMethods.getValues());
        // No payload means the font could not be completed. Passing `undefined` on to
        // `saveData` is NOT a no-op — `Pub.emit` merges (`{...state, ...value}`) and
        // `clearStream()` never resets `dataToSave`, so it would fire a mutation carrying the
        // previously accumulated payload and toast "saved" for a font change that was
        // deliberately left out (WA-3774).
        if (dataToSave) reportsConfigurationTabState.pub.saveData(dataToSave);
      } else {
        reportsConfigurationTabState.pub.saveData();
      }
    }
  };

  return (
    <>
      <ContentLoading {...{ loading: subCompanyDataLoading }}>
        <Content
          type='stack'
          stackProps={{
            flexDirection: 'row',
            height: '100%',
            ...(expandedState && { gap: 2 }),
          }}
        >
          <SStack direction='column' gap={2} opened={expandedState} pt='5px'>
            <FormProvider {...formMethods}>
              {subCompaniesOptions.length > 0 && (
                <Select
                  label={t('customer.company')}
                  options={subCompOptions}
                  placeholder={t('common.default')}
                  disableTextInput
                  onChange={(_, v) => handleSubCompanyChange(v)}
                />
              )}
              <Stack
                direction='row'
                sx={{
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Select
                  label={t('settings.print_form_type')}
                  options={printFormsTypesOptions}
                  value={printFormsTypesOptions.find((el) => el.id === printFormType)}
                  disableClearable
                  disableTextInput
                  onChange={(_, v) => handlePrintFormTypeChange(v)}
                />
                <CommonMenu />
              </Stack>

              <form style={{ width: 400 }} noValidate onBlur={onSubmit}>
                {printFormType === ConfigurablePrintFormTypes.ARTICLE_PRICELIST && (
                  <ArticlePricelistConfig
                    valuesPath='articlePricelist'
                    defaultValuesPath='articlePricelistDefaultValues'
                  />
                )}
                {printFormType === ConfigurablePrintFormTypes.OFFER && (
                  <OfferConfig valuesPath='offer' defaultValuesPath='offerDefaultValues' />
                )}
                {printFormType === ConfigurablePrintFormTypes.DELIVERY_NOTE_A4 && (
                  <DeliveryNoteA4Config
                    valuesPath='deliveryNoteA4'
                    defaultValuesPath='deliveryNoteA4DefaultValues'
                  />
                )}
                {printFormType === ConfigurablePrintFormTypes.DELIVERY_NOTE_A5 && (
                  <DeliveryNoteA5Config
                    valuesPath='deliveryNoteA5'
                    defaultValuesPath='deliveryNoteA5DefaultValues'
                  />
                )}
                {printFormType === ConfigurablePrintFormTypes.INVOICE && (
                  <InvoiceConfig valuesPath='invoice' defaultValuesPath='invoiceDefaultValues' />
                )}
                {printFormType === ConfigurablePrintFormTypes.PAYMENT_SLIP && (
                  <PaymentSlipConfig
                    valuesPath='paymentSlip'
                    defaultValuesPath='paymentSlipDefaultValues'
                  />
                )}
                {printFormType === ConfigurablePrintFormTypes.DEBT_REMINDER && (
                  <DebtReminderConfig
                    valuesPath='debtReminder'
                    defaultValuesPath='debtReminderDefaultValues'
                  />
                )}
              </form>
            </FormProvider>
          </SStack>
          <Box sx={{ width: '100%' }}>
            <ReportPreviewArea
              inputDataForGeneratePreview={inputDataForReportsPreview}
              printFormType={printFormType}
              disabledPreview={isDirty}
              onExpandedState={setExpandedState}
              expandedState={expandedState}
              subCompanyId={watch('subCompanyId')}
            />
          </Box>
        </Content>
      </ContentLoading>
    </>
  );
};

const getPathPrefix = (targetName: string): string => {
  const parts = targetName.split('.');
  parts.pop();
  return parts.join('.');
};

const getLastPartOfPath = (targetName: string): string => {
  const parts = targetName.split('.');
  const lastPart = parts[parts.length - 1];
  return lastPart;
};

const checkIsFontSettings = (lastPartOfPath: string): boolean => {
  return lastPartOfPath === 'size' || lastPartOfPath === 'family' || lastPartOfPath === 'style';
};

const checkIsAdditionalFieldRequired = (targetName: string): boolean => {
  const lastPart = getLastPartOfPath(targetName);
  return checkIsFontSettings(lastPart);
};

/** `invoice` -> `invoiceDefaultValues`; every report in this tab is wired that way. */
const toDefaultValuesPath = (path: string): string => {
  const [reportKey, ...rest] = path.split('.');
  return [`${reportKey}DefaultValues`, ...rest].join('.');
};

/**
 * The backend serializes a font as a single string, so `WA_FontDescriptionInput` requires both
 * `family` and `size` (and, when `style` is sent, all three of its flags). An empty font input
 * means "use the report default" — that is exactly what the tuner renders as its placeholder —
 * so fall back to the default font before giving up.
 *
 * Returns `undefined` when even the default is incomplete, so that a partial `fontOverride` is
 * never recorded and can never poison a later save (WA-3723 / WA-3774).
 */
const resolveCompleteFontOverride = (
  fontPath: string,
  allData: DeepPartialSkipArrayKey<ReportsConfigurationRes>,
): Wa_FontDescriptionInput | undefined => {
  const current = getJSONValueByPath(allData, fontPath);
  const defaults = getJSONValueByPath(allData, toDefaultValuesPath(fontPath));

  const family = current?.family ?? defaults?.family;
  const size = current?.size ?? defaults?.size;
  const style = current?.style ?? defaults?.style;

  if (typeof family !== 'string' || !family || typeof size !== 'number') return undefined;

  // `WA_FontStyleInput` is optional as a whole but has three non-nullable flags — omit it
  // entirely rather than sending a half-filled style object.
  const isCompleteStyle =
    !!style &&
    typeof style.isBold === 'boolean' &&
    typeof style.isItalic === 'boolean' &&
    typeof style.isUnderline === 'boolean';

  return {
    family,
    size,
    ...(isCompleteStyle
      ? {
          style: {
            isBold: style.isBold,
            isItalic: style.isItalic,
            isUnderline: style.isUnderline,
          },
        }
      : {}),
  };
};

const formDataWithAdditionalFields = (
  targetName: string,
  // `watch` reports a deep-partial snapshot, while `getValues` returns the full form values —
  // accept both, the reads below are path-based and tolerate missing branches.
  allData: DeepPartialSkipArrayKey<ReportsConfigurationRes>,
) => {
  const prefix = getPathPrefix(targetName);
  const lastPart = getLastPartOfPath(targetName);
  const isFontSettings = checkIsFontSettings(lastPart);
  let dataToSave;

  if (isFontSettings) {
    const fontOverride = resolveCompleteFontOverride(prefix, allData);
    // Never record a partial font — see resolveCompleteFontOverride (WA-3774).
    if (!fontOverride) return undefined;
    dataToSave = createNestedObject(prefix, fontOverride);
    dataToSave['subCompanyId'] = allData.subCompanyId;
    return dataToSave as IReportsConfigurationTabState['dataToSave'];
  }
};

const SStack = styled(Stack, {
  shouldForwardProp: (prop) => prop !== 'opened',
})<{ opened?: boolean }>(({ opened }) => ({
  width: opened ? 400 : 0,
  minWidth: opened ? 400 : 0,
  transition: '0.3s',
  overflowX: 'auto',
}));

export interface IReportConfigurationValuesPaths {
  valuesPath: Exclude<keyof ReportsConfigurationRes, '__typename'>;
  defaultValuesPath: Exclude<keyof ReportsConfigurationRes, '__typename'>;
}

export interface IReportAnyObjectTunerProps {
  reportActualValuesPath: Exclude<keyof ReportsConfigurationRes, '__typename'>;
  reportDefaultValuesPath: Exclude<keyof ReportsConfigurationRes, '__typename'>;
  reportObjectName: string;
}

export interface IReportImageObjectTunerProps extends IReportAnyObjectTunerProps {
  maxImgHeight?: number;
  maxImgWidth?: number;
}
